<template>
  <ChatLoading v-if="loading" />

  <router-view v-else-if="isVisibleRouterView" v-slot="{ Component }">
    <ChatTutorialOverlay :chat-type="callType" />
    <component :is="Component" />
  </router-view>

  <div v-else ref="chatRef" class="container-selfservice-chat" :style="maxHeight">
    <div class="container-chat">
      <div class="guide-btn-wrap">
        <h4 class="accent">{{ title }}</h4>
        <a class="guide-btn" @click="redirectToManualPage(manualLanguage, callType)">
          <IconGuide class="icon"></IconGuide>
        </a>
      </div>

      <SelfServiceChatModal
        :title="chatModalProperties.title"
        :mode="chatModalProperties.mode"
        :call-type="callType"
        :unit-price-list="unitPriceList"
        @create="startChat"
        @enter="enterChat"
      />
    </div>

    <div v-if="chatModalProperties.mode === 'CREATE'" :class="{ 'container-selfservice-chat-list': !showCards }">
      <CallCardlist
        v-model:cards="showCards"
        v-model:is-favorite="isFavorite"
        :title="t('ss_list_prev_calls')"
        :list-call="listCall"
        using-actions
        using-close-btn
        @select="startChat"
        @refresh="getListCall"
        @scroll-end="getListCall(true)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from 'lodash';

import { fetchSelfServiceContractPricingV2 } from '@/api/apiService';
import ChatLoading from '@/components/domains/chat/common/ChatLoading.vue';
import ChatTutorialOverlay from '@/components/domains/chat/tutorial/ChatTutorialOverlay.vue';
import IconGuide from '@/components/icons/IconGuide.vue';
import SelfServiceChatModal from '@/components/self-service/chat/SelfServiceChatModal.vue';
import CallCardlist from '@/components/shared/composite/CallCardlist.vue';
import { useChatUI } from '@/composables/domains/chat/useChatUI';
import { useModalCardManage } from '@/composables/domains/self-service/useModalCardManage';
import { useModalRefill } from '@/composables/domains/self-service/useModalRefill';
import { useModalUpsell } from '@/composables/domains/self-service/useModalUpsell';
import { useSelfServiceCallManage } from '@/composables/domains/self-service/useSelfServiceCallManage';
import { useNotification } from '@/composables/shared/useNotification';
import { useSocketController } from '@/composables/shared/useSocketController';
import { getLanguageByCode } from '@/static/languages';
import { useAdminStore } from '@/stores/admin';
import { useAudioStore } from '@/stores/audio';
import { useSocketStore } from '@/stores/socket';
import { useSubscriptionStore } from '@/stores/subscription';
import { useUserStore } from '@/stores/user';
import { LanguageCode, TypeCall } from '@/types/common';
import { ScenarioUpsell } from '@/types/components';
import { IResponseContractCustomerPricing } from '@/types/response';
import { CHAT_TYPES, CREATE_CHAT_TYPES } from '@/utils/constants/chat';
import { isInCapacitorApp } from '@/utils/device';
import { ij_bottomBarStatus, ij_chat_mode, ij_chat_ui, ij_chatId, ij_setBottomBarStatus } from '@/utils/injectionKeys';
import redirectToManualPage from '@/utils/redirectToManualPage';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const adminStore = useAdminStore();
const audioStore = useAudioStore();
const socketStore = useSocketStore();
const subscriptionStore = useSubscriptionStore();
const userStore = useUserStore();

/**
 * TODO - ChatUI 관련 상태 통합 진행 중입니다.
 * - 아래 상태들은 점진적으로 ChatUI 객체로 이전될 예정입니다.
 * 대상 상태:
 * 1) isVisibleButtonMyLanguage
 * 2) isVisibleSecretController
 * 3) isVisibleSecretLangaugeChange
 * */
const chatUI = useChatUI();
const { openModalCardManage } = useModalCardManage();
const { openModalRefill } = useModalRefill();
const { openModalUpsell } = useModalUpsell();
const { isFavorite, listCall, listFilterSearch, listTagSelected, getListCall } = useSelfServiceCallManage();
const { alertLogoutWhileInChat } = useNotification();

const { languageAway, languageHome } = storeToRefs(audioStore);

const unitPriceList = ref<IResponseContractCustomerPricing[]>([]); // 단가정보
const selectedCode = ref<string>(''); // 현재 선택된 채팅 코드
const loading = ref<boolean>(false); // 채팅 연결 로딩
const showCards = ref<boolean>(false);
/**
 * TODO
 * - 컨포넌트 내부의 소켓연결을 감지할때 사용되는 값입니다.
 * - 내부소켓연결을 부모컨포넌트(layout)에 빼내는 작업이 필요합니다.
 * - 남은 통화방: SINGLE
 * */
const isLocalSocketConnected = ref<boolean>(false);

const isMobile = isInCapacitorApp();

const manualLanguage = computed<'ko' | 'en'>(() => {
  return userStore.pageLanguage === 'ko' ? 'ko' : 'en';
});

const isVisibleRouterView = computed(() => {
  return isSocketConnected.value || isLocalSocketConnected.value;
});

const callType = computed<TypeCall>(() => {
  const { chatType = 'GENERAL' } = route.meta;
  return Object.keys(CHAT_TYPES).includes(chatType) ? chatType : 'GENERAL';
});

// 소켓 연결 유무
const isSocketConnected = computed<boolean>(() => {
  return !!selectedCode.value && socketStore.socketState !== 'CLOSED';
});

// 통화방 제목
const title = computed<string>(() => {
  return (
    ({
      GENERAL: t('label_general'),
      CONFERENCE: t('label_conference'),
      MEETING: t('label_meeting'),
      AUDIENCE: t('label_audience'),
      SINGLE: t('label_single'),
    }[callType.value] || '') + ` ${t('label_interpretation')}`
  );
});

// 채팅모달정보
const chatModalProperties = computed<Record<string, string>>(() => {
  const isCreateMode = CREATE_CHAT_TYPES.includes(callType.value);
  return {
    title: isCreateMode ? t('label_new_interpretation') : title.value,
    mode: isCreateMode ? 'CREATE' : 'ENTER',
  };
});

// TODO: 임시코드. 추후 CSS로 해결할 수 있도록 수정
const maxHeight = computed(() => {
  return adminStore.isHost &&
    (subscriptionStore.usagePercentage > 90 ||
      !!subscriptionStore.lastPaymentErrorCode ||
      !subscriptionStore.currentCredits ||
      subscriptionStore.currentCredits === 0)
    ? { 'max-height': '100%' }
    : { 'max-height': 'calc(100%) - 10rem' };
});

const { bottomBarStatus, setBottomBarStatus, setupWebsocket, connectWebsocket, disconnectWebsocket } =
  useSocketController(selectedCode, callType, 'SELF_SERVICE');

// 채팅방 상태를 초기화 합니다.
const setupChatState = async () => {
  await requestUnitPriceList();

  selectedCode.value = '';
  isLocalSocketConnected.value = false;

  const { code = '', planId = '', sourceLanguage = '', targetLanguage = '' } = route.query;

  if (targetLanguage) {
    languageHome.value = sourceLanguage
      ? getLanguageByCode(sourceLanguage)
      : getLanguageByCode(localStorage.getItem('language') ?? 'ko');
    languageAway.value = targetLanguage ? getLanguageByCode(targetLanguage) : null;
  }

  if (code && planId) {
    await startChat(
      {
        code: code.toString(),
        planId: planId.toString(),
        ...(sourceLanguage ? { sourceLanguage: String(sourceLanguage) } : {}),
        ...(targetLanguage ? { targetLanguage: String(targetLanguage) } : {}),
      },
      'SINGLE' === callType.value
    );
  } else if (chatModalProperties.value.mode === 'CREATE') {
    await requestChatList();
  }
};

// 채팅을 시작할 충분한 크래딧이 있는지 체크합니다.
const checkValidCredit = async (id: string): Promise<boolean> => {
  await subscriptionStore.fetchSubscriptionInfo();
  const { postpaidUnitPrice = 0 } = unitPriceList.value.find(({ planId }) => planId === id) || {};
  const credits = subscriptionStore.currentCredits;

  return !!credits && !!postpaidUnitPrice && Number(credits) >= Number(postpaidUnitPrice);
};

// 쿼리스트링을 업데이트합니다.
const updateRouteQuery = (query) => {
  router.replace({ query });
};

// 채팅을 시작합니다.
const startChat = async (
  info: { code: string; planId: string; sourceLanguage?: string; targetLanguage?: string },
  isLocal?: boolean
) => {
  if (loading.value) {
    return;
  }

  loading.value = true;

  if (info.sourceLanguage) {
    audioStore.selectLanguage(info.sourceLanguage as LanguageCode, true);
  }

  if (info.targetLanguage) {
    audioStore.setLanguageHome(getLanguageByCode(info.sourceLanguage));
    audioStore.setLanguageAway(getLanguageByCode(info.targetLanguage));
  }

  if (await checkValidCredit(info.planId)) {
    selectedCode.value = info.code;

    if (isLocal) {
      connectLocalWebsocket();
    } else {
      await setupWebsocket();
      await connectWebsocket(true);
    }

    updateRouteQuery({
      code: info.code,
      planId: info.planId,
      ...(info.sourceLanguage ? { sourceLanguage: info.sourceLanguage } : {}),
      ...(info.targetLanguage ? { targetLanguage: info.targetLanguage } : {}),
    });
  } else {
    await requestChatList();
    if (adminStore.isHost) {
      showUpgradeModal();
    }

    updateRouteQuery(null);
  }

  loading.value = false;
};

// 채팅방을 입장합니다.
const enterChat = async (planId: string, sourceLanguage?: string, targetLanguage?: string) => {
  await startChat(
    {
      code: userStore.userId!,
      planId,
      ...(sourceLanguage && { sourceLanguage }),
      ...('SINGLE' === callType.value ? { targetLanguage } : {}),
    },
    'SINGLE' === callType.value
  );
};

// 컨포넌트 안쪽에서 실행된 로직을 트리거 합니다.
const connectLocalWebsocket = () => {
  isLocalSocketConnected.value = true;

  const socketWatcher = watch(
    () => socketStore.socketState,
    () => {
      isLocalSocketConnected.value = false;
      socketWatcher();
    }
  );
};

const scenarioUpsell = computed((): ScenarioUpsell | null => {
  const userPlan = subscriptionStore.currentPlan;

  if (subscriptionStore.lastPaymentErrorCode) {
    return 'CARD_CHANGE';
  }
  if (userPlan === 'FREE') {
    return 'PLAN_UPGRADE';
  }

  if (['FREE', 'STARTER'].includes(userPlan)) {
    return 'PLAN_UPGRADE';
  }

  if (userPlan === 'PRO') {
    if (!subscriptionStore.currentPlanDetails?.rechargeAllow) {
      return 'REFILL_ADD';
    }

    if (subscriptionStore.isRechargeLimitFull) {
      return 'REFILL_INCREASE';
    }
  }

  return null;
});

const showUpgradeModal = () => {
  switch (scenarioUpsell.value) {
    case 'PLAN_UPGRADE':
      openModalUpsell();
      break;
    case 'REFILL_ADD':
    case 'REFILL_INCREASE':
      openModalRefill({ isRequired: false });
      break;
    case 'CARD_CHANGE':
      openModalCardManage({ isModeCheckout: false });
      break;
  }
};

/**
 * @desc 생성된 채팅방을 요청합니다.
 * **/
const requestChatList = async () => {
  listFilterSearch.value.forEach((item) => (item.isChecked = item.value === callType.value));
  listTagSelected.value = [callType.value];
  await getListCall();
};

/**
 * @desc 현재 크래딧 단가를 조회합니다.
 * **/
const requestUnitPriceList = async () => {
  const { data = [] } = await fetchSelfServiceContractPricingV2();
  unitPriceList.value = data;
};

onBeforeRouteUpdate(async (updateGuard) => {
  if (isEmpty(updateGuard.query) && socketStore.socketState !== 'CLOSED') {
    disconnectWebsocket();
    setBottomBarStatus(2);
  }
});

onMounted(async () => {
  await setupChatState();
});

// callType이 변경되면, 채팅상태를 변경합니다.
watch(
  () => callType.value,
  async () => {
    await setupChatState();
  }
);

watch(
  () => route.query,
  async () => {
    if (!isEmpty(route.query)) return;

    await requestChatList();
  }
);

watch(
  () => bottomBarStatus.value,
  async (newVal) => {
    if (newVal === 2) {
      await userStore.fetchUserInfo();
      await requestChatList();
    }
  }
);

watch(
  () => userStore.isPendingLogout,
  async (isPending) => {
    if (isPending) {
      alertLogoutWhileInChat(
        async () => {
          disconnectWebsocket();

          await userStore.requestLogout();
          router.push({ path: '/login/ss' });
          userStore.isPendingLogout = false;
        },
        () => {
          userStore.isPendingLogout = false;
        }
      );
    }
  }
);

provide(ij_chat_ui, chatUI);
provide(ij_chatId, socketStore.selectedChatId);
provide(ij_bottomBarStatus, bottomBarStatus);
provide(ij_setBottomBarStatus, setBottomBarStatus);
provide(ij_chat_mode, 'SELF_SERVICE');
</script>

<style scoped lang="scss">
.container-selfservice-chat {
  position: relative;
}
.container-chat {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 540px;
  min-width: 0;
  padding: 2rem 1.25rem 1rem;
  gap: 1rem;
  margin: 0 auto;
  overflow-y: hidden;

  @include respond-to(mobile-landscape) {
    flex: 0 0 auto;
    height: auto;
    min-height: -webkit-fit-content;
    min-height: -moz-fit-content;
    min-height: fit-content;
  }
}

.container-selfservice-chat-list {
  display: flex;
  gap: 1rem;
  height: 100%;
  max-width: 480px;
  width: 100%;
  flex: 1;
  justify-content: center;
  overflow-y: auto;
  background: #fff;
  border-left: 1px solid $color-gray-40;
  z-index: 10;
  @include respond-to(tablet) {
    max-width: 400px;
  }

  @include respond-to(mobile-landscape) {
    max-width: unset;
    width: 100%;
    overflow-y: unset;
    padding-top: 2.5rem;
  }
}

.guide-btn-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  .guide-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    background: $color-gray-40;
    border-radius: 50%;
    gap: 0.25rem;
    text-decoration: none;
    color: $color-gray-900;
    font-size: $font-size-psmall;
    letter-spacing: -0.4px;
    width: 2rem;
    height: 2rem;
    svg {
      width: 1.125rem;
      height: 1.125rem;
      object-fit: contain;
    }
  }
}
</style>
